<template>
    <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-smx tw-relative tw-p-5 tw-flex tw-flex-col tw-items-center tw-justify-between tw-min-h-[280px] tw-h-full hover:tw-shadow-md hover:tw-border-primary-300 tw-transition-all tw-duration-200 tw-cursor-pointer">
        <!-- Bookmark Icon -->
        <button
            class="tw-absolute tw-top-3 tw-right-3 tw-z-10 tw-p-1.5 tw-rounded-full hover:tw-bg-gray-100 tw-transition-colors tw-duration-150"
            @click.stop="onBookmark"
            :disabled="bookmarkLoading"
            aria-label="Bookmark"
        >
            <icon
                v-if="!bookmarkLoading"
                :icon="bookmarked ? 'solar:bookmark-bold' : 'solar:bookmark-outline'"
                class="tw-text-2xl tw-text-primary-500"
            />
            <svg v-else class="tw-animate-spin tw-h-6 tw-w-6 tw-text-primary-400" fill="none" viewBox="0 0 24 24">
                <circle class="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
        </button>

        <!-- Clickable Card Content -->
        <div 
            class="tw-w-full tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-between tw-text-decoration-none tw-cursor-pointer"
            @click="viewProduct"
        >
            <!-- Product Image -->
            <div class="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-w-full tw-mb-3">
                <div v-if="product.thumbnail_url || product.image" class="tw-w-full tw-flex tw-justify-center">
                    <img 
                        :src="product.thumbnail_url || product.image" 
                        :alt="product.name || product.title" 
                        class="tw-max-h-32 tw-object-contain tw-rounded-md tw-bg-gray-50 tw-w-auto tw-h-auto" 
                    />
                </div>
                <div v-else class="tw-w-32 tw-h-32 tw-bg-gray-100 tw-rounded-md tw-flex tw-items-center tw-justify-center">
                    <icon icon="solar:box-bold-duotone" class="tw-text-4xl tw-text-primary-300" />
                </div>
            </div>

            <!-- Product Info -->
            <div class="tw-w-full tw-flex tw-flex-col tw-items-start tw-space-y-2">
                <!-- Product Title -->
                <div class="tw-text-md tw-font-bold tw-text-gray-900 tw-leading-tight tw-line-clamp-2" :title="product.name || product.title">
                    {{ product.name || product.title }}
                </div>

                <!-- Product Description -->
                <div v-if="product.description" class="tw-text-sm tw-text-gray-600 tw-line-clamp-2 tw-leading-relaxed">
                    {{ product.description }}
                </div>

                <!-- Categories -->
                <div v-if="product.categories && product.categories.length > 0" class="tw-flex tw-gap-1 tw-overflow-hidden tw-w-full">
                    <div class="tw-flex tw-gap-1 tw-overflow-hidden tw-flex-1">
                        <span 
                            v-for="category in product.categories.slice(0, 2)" 
                            :key="category.id"
                            class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium tw-bg-primary-100 tw-text-primary-800 tw-min-w-0 tw-flex-shrink tw-overflow-hidden"
                            :title="category.name"
                        >
                            <span class="tw-truncate">{{ category.name }}</span>
                        </span>
                    </div>
                    <span 
                        v-if="product.categories.length > 2" 
                        class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium tw-bg-gray-100 tw-text-gray-800 tw-flex-shrink-0"
                        :title="`${product.categories.length - 2} more categories`"
                    >
                        +{{ product.categories.length - 2 }}
                    </span>
                </div>

                <!-- Company Info -->
                <div v-if="product.company" class="tw-text-xs tw-text-gray-500 tw-truncate tw-w-full">
                    {{ $t('common.by', 'By') }} {{ product.company.name }}
                </div>

                <!-- Price -->
                <div v-if="product.price" class="tw-text-lg tw-font-bold tw-text-primary-600">
                    {{ product.price }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, ref } from 'vue';
import bookmarksApi from '@/api/bookmarks';

const props = defineProps({
    product: {
        type: Object,
        required: true
    }
});
console.log(props.product);

const bookmarked = ref(props.product.is_bookmarked);
const bookmarkLoading = ref(false);

const onBookmark = async () => {
    bookmarkLoading.value = true;

    await bookmarksApi.toggle({
        bookmarkable_type: 'product',
        bookmarkable_id: props.product.id
    })

    bookmarked.value = !bookmarked.value;
    bookmarkLoading.value = false;
}

function formatDate(date) {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
}
formatDate

// Handle product click - for now just show an alert
// TODO: Create a shared product detail route or modal
function viewProduct() {
    console.log('Product clicked:', props.product);
    // Placeholder - could open a modal or navigate to detail page when route is created
    alert(`Product: ${props.product.name}\nDescription: ${props.product.description || 'No description'}`);
}
</script>

<style scoped>
.tw-line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style> 